/**
 * EmployeesDashboard — R49 · KPIs + intelligence for the HR team.
 *
 * One-page dashboard powered by `/employees/dashboard` + `/employees/insights`.
 * Read-only. All numbers link to the underlying list/detail pages where sensible.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import {
  Users, UserPlus, LogOut, Cake, PartyPopper, TrendingDown, AlertTriangle,
  DollarSign, Clock, ClipboardCheck, ListChecks, Gauge, Loader2, Sparkles,
} from "lucide-react";

function Kpi({ label, value, icon: Icon, sub, tone = "" }) {
  return (
    <div className="border p-3 bg-background" data-testid={`emp-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums mt-1 ${tone}`}>{value ?? "—"}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, right, children }) {
  return (
    <div className="border p-3 bg-background space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{title}</div>
        {right}
      </div>
      {children}
    </div>
  );
}

function DistBar({ items, total }) {
  const entries = Object.entries(items || {}).slice(0, 8);
  if (!entries.length) return <div className="text-xs text-muted-foreground">No data.</div>;
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="space-y-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center gap-2 text-xs">
          <div className="w-32 truncate" title={k}>{k}</div>
          <div className="flex-1 bg-neutral-100 h-3 relative">
            <div className="bg-indigo-500 h-3" style={{ width: `${(v / max) * 100}%` }} />
          </div>
          <div className="w-10 text-right tabular-nums font-semibold">{v}</div>
          {total ? <div className="w-10 text-right text-[10px] text-muted-foreground">{Math.round((v / total) * 100)}%</div> : null}
        </div>
      ))}
    </div>
  );
}

export default function EmployeesDashboard() {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    (async () => {
      setLoading(true);
      try {
        const [d, i] = await Promise.all([
          api.get(`/workspaces/${activeWorkspaceId}/employees/dashboard`),
          api.get(`/workspaces/${activeWorkspaceId}/employees/insights`),
        ]);
        setData(d.data); setInsights(i.data?.items || []);
      } catch (e) { toast.error(apiErrorMessage(e, "Failed to load dashboard")); }
      finally { setLoading(false); }
    })();
  }, [activeWorkspaceId]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading employee dashboard…</div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">No data yet.</div>;

  const k = data.kpis, p = data.pipeline, u = data.utilisation, c = data.celebrations;
  const g = data.gaps, a = data.attendance, comp = data.comp, ob = data.onboarding_offboarding;

  return (
    <>
      <PageHeader
        eyebrow="Employees · Intelligence"
        title="HR dashboard"
        description="Headcount, joining pipeline, utilisation, celebrations, gaps, attendance, comp intel + onboarding/offboarding — all in one place."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4" data-testid="employees-dashboard">
        {/* Callouts */}
        {insights.length > 0 && (
          <div className="border p-3 bg-gradient-to-br from-amber-50 to-transparent" data-testid="employees-insights">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1"><Sparkles className="w-3 h-3" /> Smart callouts</div>
            <div className="grid md:grid-cols-2 gap-1.5">
              {insights.map((it, i) => {
                const tone = { info: "bg-sky-100 text-sky-800 border-sky-300",
                                 warn: "bg-amber-100 text-amber-800 border-amber-300",
                                 danger: "bg-rose-100 text-rose-800 border-rose-300" }[it.severity] || "bg-neutral-100";
                return <div key={i} className={`text-xs border px-2 py-1 ${tone}`} data-testid={`emp-insight-${i}`}>{it.text}</div>;
              })}
            </div>
          </div>
        )}

        {/* 1 · Headcount KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
          <Kpi label="Active" value={k.total_active} icon={Users} sub={`${k.total_all} total on roster`} />
          <Kpi label="Joining this month" value={p.joiners_this_month} icon={UserPlus} />
          <Kpi label="Open offers" value={p.open_offers} icon={ClipboardCheck} />
          <Kpi label="On probation" value={k.on_probation} icon={Clock} sub={`${p.probation_ending_30d.length} ending in 30d`} />
          <Kpi label="Exits · 12mo" value={k.exits_last_12mo} icon={LogOut} sub={`${k.exits_this_month} this month`} tone="text-rose-700" />
          <Kpi label="Attrition YTD" value={`${k.attrition_pct_ytd}%`} icon={TrendingDown} tone={k.attrition_pct_ytd >= 10 ? "text-rose-700" : ""} />
        </div>

        <div className="grid md:grid-cols-2 gap-3">
          <Section title="Headcount by department"><DistBar items={k.by_department} total={k.total_active} /></Section>
          <Section title="Headcount by designation"><DistBar items={k.by_designation} total={k.total_active} /></Section>
          <Section title="Headcount by location"><DistBar items={k.by_location} total={k.total_active} /></Section>
          <Section title="Employment type"><DistBar items={k.by_employment_type} total={k.total_active} /></Section>
        </div>

        {/* 3 · Utilisation */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Kpi label="Hours logged · 30d" value={u.total_hours_last_30d} icon={Gauge} />
          <Kpi label="Billable · 30d" value={u.billable_hours_last_30d} icon={Gauge} tone="text-emerald-700" />
          <Kpi label="Billable %" value={`${u.billable_ratio_pct}%`} icon={Gauge} tone={u.billable_ratio_pct >= 60 ? "text-emerald-700" : "text-amber-700"} />
          <Kpi label="Avg active projects/emp" value={u.avg_active_projects_per_emp} icon={ListChecks} />
        </div>

        {/* 4 · Celebrations + retention */}
        <div className="grid md:grid-cols-3 gap-3">
          <Section title="🎉 Anniversaries this week">
            {c.anniversaries_this_week.length === 0 ? <div className="text-xs text-muted-foreground">None this week.</div> : (
              <div className="space-y-1">
                {c.anniversaries_this_week.map((x) => (
                  <div key={x.id} className="text-xs flex items-center gap-2" data-testid={`emp-anniversary-${x.id}`}>
                    <PartyPopper className="w-3 h-3 text-amber-500" />
                    <span className="font-medium">{x.full_name}</span>
                    <span className="text-muted-foreground">· {x.years}yr</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
          <Section title="🎂 Birthdays this week">
            {c.birthdays_this_week.length === 0 ? <div className="text-xs text-muted-foreground">None this week.</div> : (
              <div className="space-y-1">
                {c.birthdays_this_week.map((x) => (
                  <div key={x.id} className="text-xs flex items-center gap-2"><Cake className="w-3 h-3 text-pink-500" /> {x.full_name}</div>
                ))}
              </div>
            )}
          </Section>
          <Section title="Retention buckets">
            <div className="grid grid-cols-4 gap-1">
              {[["lt_1yr", "<1yr"], ["1_3yr", "1-3yr"], ["3_5yr", "3-5yr"], ["gt_5yr", "5+yr"]].map(([key, label]) => (
                <div key={key} className="border p-2 text-center">
                  <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
                  <div className="text-lg font-bold tabular-nums">{c.retention_buckets[key] || 0}</div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* 5 · Gaps */}
        <Section title="Skill / designation gaps" right={<span className="text-xs text-muted-foreground">{g.open_roles} open roles</span>}>
          {g.designation_gaps.length === 0 ? <div className="text-xs text-muted-foreground">No under-filled designations detected.</div> : (
            <div className="grid md:grid-cols-2 gap-1.5">
              {g.designation_gaps.map((d, i) => (
                <div key={i} className="text-xs border p-2 flex items-center gap-2" data-testid={`emp-gap-${i}`}>
                  <AlertTriangle className="w-3 h-3 text-amber-600" />
                  <div className="flex-1">
                    <div className="font-medium">{d.designation}</div>
                    <div className="text-[10px] text-muted-foreground">Plan {d.planned} · Actual {d.actual}</div>
                  </div>
                  <div className="font-bold text-rose-700">-{d.gap}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* 6 · Attendance */}
        <div className="grid grid-cols-3 gap-2">
          <Kpi label="Present today" value={a.present_today} icon={ClipboardCheck} tone="text-emerald-700" />
          <Kpi label="On leave today" value={a.on_leave_today} icon={Clock} />
          <Kpi label="Pending approvals" value={a.pending_leave_approvals} icon={ClipboardCheck} tone={a.pending_leave_approvals > 0 ? "text-amber-700" : ""} />
        </div>

        {/* 7 · Comp intel */}
        <div className="grid md:grid-cols-2 gap-3">
          <Section title="Avg CTC by department">
            {Object.keys(comp.avg_ctc_by_dept).length === 0 ? <div className="text-xs text-muted-foreground">No CTC captured yet.</div> : (
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-left">
                  <tr><th className="p-1">Dept</th><th className="p-1 text-right">Avg</th><th className="p-1 text-right">n</th></tr>
                </thead>
                <tbody>
                  {Object.entries(comp.avg_ctc_by_dept).map(([d, v]) => (
                    <tr key={d} className="border-t"><td className="p-1">{d}</td><td className="p-1 text-right tabular-nums">{v.currency} {v.avg?.toLocaleString?.() || v.avg}</td><td className="p-1 text-right text-muted-foreground">{v.n}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
          <Section title="Avg CTC by gender" right={<span className="text-[10px] text-muted-foreground">Pay-parity view</span>}>
            {Object.keys(comp.avg_ctc_by_gender).length === 0 ? <div className="text-xs text-muted-foreground">No CTC captured yet.</div> : (
              <table className="w-full text-xs">
                <thead className="bg-neutral-50 text-left"><tr><th className="p-1">Gender</th><th className="p-1 text-right">Avg</th><th className="p-1 text-right">n</th></tr></thead>
                <tbody>
                  {Object.entries(comp.avg_ctc_by_gender).map(([gg, v]) => (
                    <tr key={gg} className="border-t"><td className="p-1 capitalize">{gg}</td><td className="p-1 text-right tabular-nums">{v.currency} {v.avg?.toLocaleString?.() || v.avg}</td><td className="p-1 text-right text-muted-foreground">{v.n}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </Section>
        </div>

        {/* 8 · Onboarding / offboarding */}
        <div className="grid md:grid-cols-2 gap-3">
          <Section title="Onboarding · in flight">
            <div className="text-3xl font-bold tabular-nums">{ob.onboardings_in_progress}</div>
            {p.upcoming_joiners.length > 0 && (
              <div className="mt-2 space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Upcoming joiners</div>
                {p.upcoming_joiners.map((x) => (
                  <div key={x.id} className="text-xs flex items-center gap-2" data-testid={`emp-upcoming-${x.id}`}>
                    <UserPlus className="w-3 h-3 text-emerald-600" />
                    <span className="font-medium">{x.full_name}</span>
                    <span className="text-muted-foreground">· {x.designation || "TBD"}</span>
                    <span className="ml-auto text-muted-foreground tabular-nums">{x.joining_date}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
          <Section title="Offboarding · pending exits">
            <div className="text-3xl font-bold tabular-nums text-rose-700">{ob.pending_exits.length}</div>
            {ob.pending_exits.length > 0 && (
              <div className="mt-2 space-y-1">
                {ob.pending_exits.map((x) => (
                  <div key={x.id} className="text-xs flex items-center gap-2" data-testid={`emp-exit-${x.id}`}>
                    <LogOut className="w-3 h-3 text-rose-500" />
                    <span className="font-medium">{x.full_name}</span>
                    <span className="text-muted-foreground truncate">· {x.exit_reason || "—"}</span>
                    <span className="ml-auto text-muted-foreground tabular-nums">{x.exit_date}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        <div className="text-[10px] text-muted-foreground text-right">Generated {new Date(data.generated_at).toLocaleString()}</div>
      </div>
    </>
  );
}
